import React, { useContext } from "react";
import { useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { supabaseManager } from "@/services/supabaseManager";
import OrganizationSelector from "@/components/OrganizationSelector";
import { useSavedAccounts } from "@/hooks/multipleAccounts/useSavedAccounts";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import DashboardLayout from "@/Layout/DashboardLayout";
import Header from "@/Layout/Header";
import { navigateToAuth } from "@/utils/navigationUtils";
import { BoltLoader } from "@/components/ui/bolt-loader";


const Index = () => {
  const location = useLocation();
  const isFastSwitch = location.pathname === "/fast-switch";
  const skipOrgLoader = !!location.state?.skipOrgLoader;
  const { loading: authLoading } = useAuth();
  const {
    currentOrganization,
    isLoading: orgLoading,
    isError: orgError,
    isFetched: orgFetched,
  } = useOrganization();
  
  const { translator } = useContext(LanguageProviderContext);
  const { accounts } = useSavedAccounts();
  const fastSwitchRecoveryShell = isFastSwitch && accounts.length > 0;
  const shouldShowLoadingShell =
  !isFastSwitch && !skipOrgLoader && ((authLoading && !currentOrganization) || orgLoading);
  const shouldShowOrgErrorShell = !isFastSwitch && orgError && !currentOrganization;
  const shouldShowNoOrgShell =
    !currentOrganization &&
    !fastSwitchRecoveryShell &&
    orgFetched &&
    !shouldShowLoadingShell &&
    !shouldShowOrgErrorShell;

  const handleReconnect = () => {
    const storedSlug = localStorage.getItem("currentOrganizationSlug");
    if (!storedSlug) return;
    supabaseManager.connectToOrganizationBySlug(storedSlug)
      .then(() => window.location.reload())
      .catch(console.error);
  };

  if (shouldShowLoadingShell) {
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{translator('Global.loading')}</CardTitle>
            <CardDescription>{translator('Home.pleaseWaitWhileLoadingData')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <BoltLoader size="lg" className="my-4" />
              <p className="text-sm text-muted-foreground mt-2">
                {authLoading 
                  ? translator('Home.verifyingUserCredentials')
                  : translator('Home.loadingOrganizationInfo')}
              </p>
              <Button
                variant="link"
                className="px-1 text-sm mt-4"
                onClick={() => window.location.reload()}
              >
                {translator('Home.reloadPage')}
              </Button>
              
              <div className="mt-4 w-full">
                <p className="text-sm text-muted-foreground mb-2 text-center">
                  {translator('Home.ifContinueHavingProblems')}
                </p>
                <div className="flex justify-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReconnect}
                  >
                    {translator('Home.reconnect')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigateToAuth();
                    }}
                  >
                    {translator('Home.goToLogin')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (shouldShowOrgErrorShell) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{translator('Home.loadingOrganizationInfo')}</CardTitle>
            <CardDescription>{translator('Home.ifContinueHavingProblems')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleReconnect}>
                {translator('Home.reconnect')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                {translator('Home.reloadPage')}
              </Button>
              <Button variant="outline" size="sm" onClick={navigateToAuth}>
                {translator('Home.goToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (shouldShowNoOrgShell) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{translator('Home.welcomeToTabstr')}</CardTitle>
            <CardDescription>
              {translator('Home.createAOrganizationMessage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OrganizationSelector />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {!isFastSwitch && <Header />}
      <DashboardLayout />
    </div>
  );
};

export default Index;
